import React from 'react';
import { EMZI_COLORS, EMZI_RADIUS, EMZI_SPACING } from '../../styles/emziCI';
import StructuredListInput from './StructuredListInput';
import { BILINGUAL_REQUIRED } from '../../utils/formValidation';

/**
 * EnhancedTrendForm - Trend pack form with Input Quality Layer enhancements
 * Focus: Market trends, intelligence, insights (SINGLE LANGUAGE - internal)
 */
const EnhancedTrendForm = ({ 
  content = {}, 
  onChange, 
  errors = {}, 
  touched = {},
  onFieldBlur,
  packType = 'trend'
}) => {
  const handleChange = (field, value) => {
    onChange({ ...content, [field]: value });
  };

  return (
    <div style={{ 
      display: 'flex',
      flexDirection: 'column',
      gap: EMZI_SPACING.md,
    }}>
      {/* Single Language Notice */}
      <div style={{
        padding: EMZI_SPACING.sm,
        background: '#EFF6FF',
        borderRadius: EMZI_RADIUS.sm,
        fontSize: '12px',
        color: '#1E40AF',
        borderLeft: `3px solid ${EMZI_COLORS.info}`,
      }}>
        📊 <strong>Internal Intelligence:</strong> Trend packs track market insights for internal strategy. Single-language (English) is sufficient.
      </div>

      {/* Trend Name/Title */}
      <div style={{ marginBottom: EMZI_SPACING.md }}>
        <label style={{
          display: 'block',
          fontSize: '14px',
          fontWeight: '500',
          marginBottom: EMZI_SPACING.xs,
          color: EMZI_COLORS.textPrimary,
        }}>
          Trend Name/Title
          <span style={{ color: EMZI_COLORS.error, marginLeft: '4px' }}>*</span>
        </label>
        
        <div style={{
          fontSize: '12px',
          color: EMZI_COLORS.textSecondary,
          marginBottom: EMZI_SPACING.xs,
          padding: EMZI_SPACING.xs,
          background: EMZI_COLORS.surface,
          borderRadius: EMZI_RADIUS.sm,
          borderLeft: `3px solid ${EMZI_COLORS.primary}`,
        }}>
          💡 Brief, descriptive name. Example: "Rising demand for plant-based supplements among Gen Z Muslims"
        </div>

        <input
          type="text"
          value={content.trend_name || ''}
          onChange={(e) => handleChange('trend_name', e.target.value)}
          onBlur={() => onFieldBlur && onFieldBlur('trend_name')}
          placeholder="e.g., Rising demand for plant-based supplements among Gen Z Muslims"
          style={{
            width: '100%',
            padding: EMZI_SPACING.sm,
            border: `1px solid ${touched.trend_name && errors.content?.trend_name ? EMZI_COLORS.error : EMZI_COLORS.border}`,
            borderRadius: EMZI_RADIUS.md,
            fontSize: '14px',
            outline: 'none',
          }}
        />
        {touched.trend_name && errors.content?.trend_name && (
          <div style={{
            marginTop: '4px',
            fontSize: '12px',
            color: EMZI_COLORS.error,
          }}>
            {errors.content.trend_name}
          </div>
        )}
      </div>

      {/* Trend Summary */}
      <div style={{ marginBottom: EMZI_SPACING.md }}>
        <label style={{
          display: 'block',
          fontSize: '14px',
          fontWeight: '500',
          marginBottom: EMZI_SPACING.xs,
          color: EMZI_COLORS.textPrimary,
        }}>
          Trend Summary
          <span style={{ color: EMZI_COLORS.error, marginLeft: '4px' }}>*</span>
        </label>
        
        <div style={{
          fontSize: '12px',
          color: EMZI_COLORS.textSecondary,
          marginBottom: EMZI_SPACING.xs,
          padding: EMZI_SPACING.xs,
          background: EMZI_COLORS.surface,
          borderRadius: EMZI_RADIUS.sm,
          borderLeft: `3px solid ${EMZI_COLORS.primary}`,
        }}>
          💡 What's happening and why it matters. Example: "Younger consumers prefer plant-based options due to environmental + health concerns"
        </div>

        <textarea
          value={content.trend_summary || ''}
          onChange={(e) => handleChange('trend_summary', e.target.value)}
          onBlur={() => onFieldBlur && onFieldBlur('trend_summary')}
          placeholder="e.g., Younger consumers prefer plant-based options due to environmental + health concerns"
          rows={4}
          style={{
            width: '100%',
            padding: EMZI_SPACING.sm,
            border: `1px solid ${touched.trend_summary && errors.content?.trend_summary ? EMZI_COLORS.error : EMZI_COLORS.border}`,
            borderRadius: EMZI_RADIUS.md,
            fontSize: '14px',
            fontFamily: 'inherit',
            outline: 'none',
            resize: 'vertical',
          }}
        />
        {touched.trend_summary && errors.content?.trend_summary && (
          <div style={{
            marginTop: '4px',
            fontSize: '12px',
            color: EMZI_COLORS.error,
          }}>
            {errors.content.trend_summary}
          </div>
        )}
      </div>

      {/* Key Signals/Indicators */}
      <StructuredListInput
        label="Key Signals/Indicators"
        items={content.key_signals || []}
        onChange={(items) => handleChange('key_signals', items)}
        minItems={1}
        maxItems={10}
        placeholder="Add a signal..."
        helper="Evidence of this trend. Example: 'Instagram hashtag #PlantBasedMalaysia up 200%' or 'Survey: 65% prefer vegan options'"
        required={true}
        hasError={touched.key_signals && errors.content?.key_signals}
        errorMessage={errors.content?.key_signals}
      />

      {/* Target Demographics */}
      <div style={{ marginBottom: EMZI_SPACING.md }}>
        <label style={{
          display: 'block',
          fontSize: '14px',
          fontWeight: '500',
          marginBottom: EMZI_SPACING.xs,
          color: EMZI_COLORS.textPrimary,
        }}>
          Target Demographics
          <span style={{
            fontSize: '11px',
            color: EMZI_COLORS.textMuted,
            marginLeft: EMZI_SPACING.xs,
            fontWeight: '400',
          }}>
            (Optional)
          </span>
        </label>
        
        <div style={{
          fontSize: '12px',
          color: EMZI_COLORS.textSecondary,
          marginBottom: EMZI_SPACING.xs,
          padding: EMZI_SPACING.xs,
          background: EMZI_COLORS.surface,
          borderRadius: EMZI_RADIUS.sm,
          borderLeft: `3px solid ${EMZI_COLORS.primary}`,
        }}>
          💡 Who's driving this trend. Example: "Gen Z (18-28), urban, college-educated, environmentally conscious"
        </div>

        <textarea
          value={content.target_demographics || ''}
          onChange={(e) => handleChange('target_demographics', e.target.value)}
          placeholder="e.g., Gen Z (18-28), urban, college-educated, environmentally conscious"
          rows={2}
          style={{
            width: '100%',
            padding: EMZI_SPACING.sm,
            border: `1px solid ${EMZI_COLORS.border}`,
            borderRadius: EMZI_RADIUS.md,
            fontSize: '14px',
            fontFamily: 'inherit',
            outline: 'none',
            resize: 'vertical',
          }}
        />
      </div>

      {/* Business Implications */}
      <StructuredListInput
        label="Business Implications"
        items={content.business_implications || []}
        onChange={(items) => handleChange('business_implications', items)}
        minItems={0}
        maxItems={8}
        placeholder="Add an implication..."
        helper="What this means for us. Example: 'Consider launching plant-based product line' or 'Update messaging to appeal to younger audience'"
        required={false}
        hasError={false}
      />

      {/* Recommended Actions */}
      <StructuredListInput
        label="Recommended Actions"
        items={content.recommended_actions || []}
        onChange={(items) => handleChange('recommended_actions', items)}
        minItems={0}
        maxItems={10}
        placeholder="Add a recommended action..."
        helper="What we should do. Example: 'Test vegan formulation' or 'Run focus group with Gen Z consumers'"
        required={false}
        hasError={false}
      />

      {/* Competitive Landscape */}
      <div style={{ marginBottom: EMZI_SPACING.md }}>
        <label style={{
          display: 'block',
          fontSize: '14px',
          fontWeight: '500',
          marginBottom: EMZI_SPACING.xs,
          color: EMZI_COLORS.textPrimary,
        }}>
          Competitive Landscape
          <span style={{
            fontSize: '11px',
            color: EMZI_COLORS.textMuted,
            marginLeft: EMZI_SPACING.xs,
            fontWeight: '400',
          }}>
            (Optional)
          </span>
        </label>
        
        <div style={{
          fontSize: '12px',
          color: EMZI_COLORS.textSecondary,
          marginBottom: EMZI_SPACING.xs,
          padding: EMZI_SPACING.xs,
          background: EMZI_COLORS.surface,
          borderRadius: EMZI_RADIUS.sm,
          borderLeft: `3px solid ${EMZI_COLORS.primary}`,
        }}>
          💡 What competitors are doing. Example: "Brand X launched vegan line Q3 2024, saw 40% sales growth"
        </div>

        <textarea
          value={content.competitive_landscape || ''}
          onChange={(e) => handleChange('competitive_landscape', e.target.value)}
          placeholder="e.g., Brand X launched vegan line Q3 2024, saw 40% sales growth"
          rows={3}
          style={{
            width: '100%',
            padding: EMZI_SPACING.sm,
            border: `1px solid ${EMZI_COLORS.border}`,
            borderRadius: EMZI_RADIUS.md,
            fontSize: '14px',
            fontFamily: 'inherit',
            outline: 'none',
            resize: 'vertical',
          }}
        />
      </div>

      {/* Trend Lifespan */}
      <div style={{ marginBottom: EMZI_SPACING.md }}>
        <label style={{
          display: 'block',
          fontSize: '14px',
          fontWeight: '500',
          marginBottom: EMZI_SPACING.xs,
          color: EMZI_COLORS.textPrimary,
        }}>
          Trend Lifespan Estimate
          <span style={{
            fontSize: '11px',
            color: EMZI_COLORS.textMuted,
            marginLeft: EMZI_SPACING.xs,
            fontWeight: '400',
          }}>
            (Optional)
          </span>
        </label>
        
        <select
          value={content.trend_lifespan || ''}
          onChange={(e) => handleChange('trend_lifespan', e.target.value)}
          style={{
            width: '100%',
            padding: EMZI_SPACING.sm,
            border: `1px solid ${EMZI_COLORS.border}`,
            borderRadius: EMZI_RADIUS.md,
            fontSize: '14px',
            outline: 'none',
            background: EMZI_COLORS.background,
          }}
        >
          <option value="">Select lifespan...</option>
          <option value="short">Short-term (&lt; 6 months)</option>
          <option value="medium">Medium-term (6-18 months)</option>
          <option value="long">Long-term (&gt; 18 months)</option>
          <option value="permanent">Permanent shift</option>
        </select>
      </div>

      {/* Sources */}
      <StructuredListInput
        label="Sources/References"
        items={content.sources || []}
        onChange={(items) => handleChange('sources', items)}
        minItems={0}
        maxItems={10}
        placeholder="Add a source..."
        helper="Where you found this data. Example: 'Nielsen Report Q4 2024' or 'Instagram trend analysis' or 'Customer survey Oct 2024'"
        required={false}
        hasError={false}
      />

      {/* Completion hint */}
      <div style={{
        padding: EMZI_SPACING.sm,
        background: '#e6f7ed',
        borderRadius: EMZI_RADIUS.sm,
        fontSize: '12px',
        color: EMZI_COLORS.success,
        borderLeft: `3px solid ${EMZI_COLORS.success}`,
      }}>
        ✅ <strong>Pro Tip:</strong> Trend packs help leadership make informed decisions. Include signals, implications, and recommended actions!
      </div>
    </div>
  );
};

export default EnhancedTrendForm;
